import { injectable } from 'inversify';
import { LogLevels } from '../constants/common';
import * as client from '../types/client';
import { InitEffectsSDKParams } from '../types/media';
import { GET_STATS_DEFAULT_INTERVAL_MS } from '../constants/stats';

type ParamsToOmit = 'clientEventEmitter' | 'internalEventEmitter' | 'network' | 'effectsSDKParams' | 'asdk';

export type ProcessedParams = Required<Omit<client.ClientParams, ParamsToOmit>> & {
  signalingServerUrl: string;
  analyticsApiUrl: string;
  effectsSDKParams: InitEffectsSDKParams;
  asdk: {
    customerId: string;
    version: string;
    localDir?: string;
    sdk_url?: string;
    api_url?: string;
  };
};

const asdkDefaults: ProcessedParams['asdk'] = {
  customerId: String(process.env.ASDK_CUSTOMER_ID),
  sdk_url: String(process.env.ASDK_SDK_URL),
  api_url: String(process.env.ASDK_API_URL),
  version: '2.3.5',
};

const esdkDefaults: InitEffectsSDKParams = {
  customerId: String(process.env.ESDK_CUSTOMER_ID),
  sdk_url: String(process.env.ESDK_SDK_URL),
  api_url: String(process.env.ESDK_API_URL),
};

const noop = (): void => { /* no operation */ };

@injectable()
export default class ConfigService {
  #params: ProcessedParams;

  constructor(params: client.ClientParams) {
    this.#params = {
      denoiser: params.denoiser ?? 'rnnoise',
      asdk: { ...asdkDefaults, ...params.asdk },
      signalingServerUrl: params.signalingServerUrl ?? String(process.env.LIVEDIGITAL_SIGNALING_SERVER_BASE_URL),
      analyticsApiUrl: params.analyticsApiUrl ?? String(process.env.LIVEDIGITAL_APP_ANALYTICS_API_BASE_URL),
      logLevel: params.logLevel ?? LogLevels.Error,
      sendAnalytics: params.sendAnalytics ?? false,
      onLogMessage: params.onLogMessage ?? noop,
      onIssues: params.onIssues ?? noop,
      disableWid: params.disableWid ?? false,
      effectsSDKParams: { ...esdkDefaults, ...params.effectsSDKParams },
      onNetworkScoresUpdated: params.onNetworkScoresUpdated ?? noop,
      getStatsInterval: params.getStatsInterval ?? GET_STATS_DEFAULT_INTERVAL_MS,
      /* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
         -- params.staticFilesPath could be empty string since it comes from ENV variable */
      staticFilesPath: params.staticFilesPath || window.location.origin,
    };
  }

  get<K extends keyof ProcessedParams>(key: K): ProcessedParams[K] {
    return this.#params[key];
  }

  set<K extends keyof ProcessedParams>(key: K, value: ProcessedParams[K]): void {
    this.#params[key] = value;
  }
}
